import { HubConnectionBuilder, HttpTransportType, LogLevel } from "@microsoft/signalr";
import {
  AIR_ORDER_CHANGE_EVENT,
  AIR_ORDER_HUB_TARGETS,
  CHAT_REFRESH_TARGETS,
  ORDER_CHANGE_EVENT,
  ORDER_HUB_TARGETS,
} from "./orderRealtimeEvents";

const FIRST_COOLDOWN_MS = 60000;
const SECOND_COOLDOWN_MS = 180000;

class ChatHub {
  constructor() {
    this.connection = null;
    this.isConnected = false;
    this.isConnecting = false;
    this.connectPromise = null;
    this.disconnectPromise = null;
    this.companyId = null;
    this.clearCallbacksOnDisconnect = false;
    this.callbacks = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectTimeout = null;
    this.sessionVersion = 0;
    this.circuitStage = 0;
    this.retryAllowedAt = 0;
    this.recoveryListenersAttached = false;
    this.handleRecoverySignal = this.handleRecoverySignal.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
  }

  connect(companyId, { isRecoveryProbe = false } = {}) {
    const normalizedCompanyId = this.normalizeCompanyId(companyId);
    if (!normalizedCompanyId) {
      return Promise.resolve();
    }

    if (this.disconnectPromise) {
      console.log("SignalR disconnect in progress, waiting before connecting...");
      return this.disconnectPromise.then(() => this.connect(normalizedCompanyId, { isRecoveryProbe }));
    }

    this.setupRecoveryListeners();

    if (this.companyId && this.companyId !== normalizedCompanyId) {
      return this.replaceCompanySession(normalizedCompanyId, isRecoveryProbe);
    }

    if (this.connectPromise) {
      console.log("SignalR connection already in progress, reusing it...");
      return this.connectPromise;
    }

    if (this.hasActiveSession()) {
      console.log("SignalR session already active, skipping...");
      return Promise.resolve();
    }

    if (this.circuitStage > 0 && !isRecoveryProbe) {
      return this.requestCircuitProbe();
    }

    this.clearReconnectTimeout();
    this.isConnecting = true;
    this.companyId = normalizedCompanyId;
    console.log("Starting SignalR connection...");

    const connection = new HubConnectionBuilder()
      .withUrl("https://websocket.dev.biny.kz/chat-hub", {
        transport: HttpTransportType.WebSockets,
        skipNegotiation: true,
        accessTokenFactory: () => JSON.parse(localStorage.getItem("authData") || "{}").token || "",
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          if (retryContext.previousRetryCount >= this.maxReconnectAttempts) {
            return null;
          }

          return retryContext.previousRetryCount < 3 ? 2000 : 5000;
        },
      })
      .configureLogging(LogLevel.Debug)
      .build();

    const sessionVersion = ++this.sessionVersion;
    this.connection = connection;
    this.setupEventHandlers(connection, sessionVersion);

    const connectPromise = this.startConnection(
      connection,
      sessionVersion,
      normalizedCompanyId,
      isRecoveryProbe,
    );
    this.connectPromise = connectPromise;
    return connectPromise;
  }

  async startConnection(connection, sessionVersion, companyId, isRecoveryProbe) {
    let connectionStarted = false;

    try {
      await connection.start();
      connectionStarted = true;

      if (!this.isCurrentSession(connection, sessionVersion)) {
        await this.stopConnection(connection);
        return;
      }

      await this.joinCompanyRoom(connection, sessionVersion, companyId);

      if (!this.isCurrentSession(connection, sessionVersion)) {
        await this.stopConnection(connection);
        return;
      }

      this.isConnected = true;
      this.isConnecting = false;
      this.resetCircuit();

      console.log("SignalR Connected");
      this.notifyCallbacks("connection-status", { connected: true });
    } catch (error) {
      if (!this.isCurrentSession(connection, sessionVersion)) {
        return;
      }

      console.error("SignalR Connection Error:", error);
      this.isConnected = false;
      this.isConnecting = false;
      this.notifyCallbacks("connection-status", { connected: false, error });
      if (connectionStarted) {
        await this.stopConnection(connection);
      }

      if (isRecoveryProbe) {
        this.handleProbeFailure();
      } else {
        this.scheduleReconnect();
      }
    } finally {
      if (this.isCurrentSession(connection, sessionVersion)) {
        this.connectPromise = null;
      }
    }
  }

  setupEventHandlers(connection, sessionVersion) {
    if (!connection) return;

    this.registerTargetGroup(connection, sessionVersion, ORDER_HUB_TARGETS, ORDER_CHANGE_EVENT);
    this.registerTargetGroup(
      connection,
      sessionVersion,
      AIR_ORDER_HUB_TARGETS,
      AIR_ORDER_CHANGE_EVENT,
    );

    connection.on("SendMessage", (data) => {
      if (!this.isCurrentSession(connection, sessionVersion)) return;

      console.log("Received SendMessage event:", data);
      try {
        const messageData = typeof data === "string" ? JSON.parse(data) : data;
        this.notifyCallbacks("order-chat", messageData);
      } catch (error) {
        console.error("Error parsing SendMessage data:", error);
      }
    });

    connection.on("PartnerMessage", (data) => {
      if (!this.isCurrentSession(connection, sessionVersion)) return;

      console.log("Received PartnerMessage event:", data);
      try {
        const messageData = typeof data === "string" ? JSON.parse(data) : data;
        this.notifyCallbacks("partner-chat", messageData);
      } catch (error) {
        console.error("Error parsing PartnerMessage data:", error);
      }
    });

    CHAT_REFRESH_TARGETS.forEach((target) => {
      connection.on(target, (data) => {
        if (!this.isCurrentSession(connection, sessionVersion)) return;

        console.log(`Received ${target} event:`, data);
        try {
          const messageData = typeof data === "string" ? JSON.parse(data) : data;
          this.notifyCallbacks("order-message-chat", messageData);
          const orderEvent =
            target === "AirOrderMessage" ? AIR_ORDER_CHANGE_EVENT : ORDER_CHANGE_EVENT;
          this.notifyCallbacks(orderEvent, { target, data: messageData });
        } catch (error) {
          console.error(`Error parsing ${target} data:`, error);
        }
      });
    });

    connection.onclose((error) => {
      if (!this.isCurrentSession(connection, sessionVersion)) return;

      console.log("SignalR Connection Closed:", error);
      this.isConnected = false;
      this.isConnecting = false;
      this.notifyCallbacks("connection-status", { connected: false, error });
      this.openCircuit();
    });

    connection.onreconnecting((error) => {
      if (!this.isCurrentSession(connection, sessionVersion)) return;

      console.log("SignalR Reconnecting:", error);
      this.isConnected = false;
      this.isConnecting = true;
      this.notifyCallbacks("connection-status", { connected: false, reconnecting: true });
    });

    connection.onreconnected(async (connectionId) => {
      if (!this.isCurrentSession(connection, sessionVersion)) return;

      console.log("SignalR Reconnected:", connectionId);
      try {
        await this.joinCompanyRoom(connection, sessionVersion, this.companyId);

        if (!this.isCurrentSession(connection, sessionVersion)) return;

        this.isConnected = true;
        this.isConnecting = false;
        this.resetCircuit();
        this.notifyCallbacks("connection-status", { connected: true, reconnected: true });
      } catch (error) {
        if (!this.isCurrentSession(connection, sessionVersion)) return;

        console.error("SignalR company room rejoin error:", error);
        this.isConnected = false;
        this.isConnecting = false;
        this.notifyCallbacks("connection-status", { connected: false, error });
        await this.stopConnection(connection);
        this.scheduleReconnect();
      }
    });
  }

  registerTargetGroup(connection, sessionVersion, targets, eventName) {
    targets.forEach((target) => {
      connection.on(target, (data) => {
        if (!this.isCurrentSession(connection, sessionVersion)) return;

        try {
          const eventData = typeof data === "string" ? JSON.parse(data) : data;
          this.notifyCallbacks(eventName, { target, data: eventData });
        } catch (error) {
          console.error(`Error parsing ${target} data:`, error);
        }
      });
    });
  }

  joinCompanyRoom(connection, sessionVersion, companyId) {
    if (!this.isCurrentSession(connection, sessionVersion) || !companyId) {
      return Promise.resolve();
    }

    return connection.invoke("JoinRoom", companyId);
  }

  replaceCompanySession(companyId, isRecoveryProbe) {
    return this.disconnect({ preserveCallbacks: true }).then(() =>
      this.connect(companyId, { isRecoveryProbe }),
    );
  }

  scheduleReconnect() {
    if (this.reconnectTimeout || this.disconnectPromise || this.hasActiveSession()) {
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log("Max reconnect attempts reached");
      this.openCircuit();
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000); // Exponential backoff, max 30s

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;

      if (this.disconnectPromise || this.hasActiveSession()) {
        return;
      }

      console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
      this.connect(this.companyId);
    }, delay);
  }

  openCircuit() {
    if (this.circuitStage > 0) return;

    this.circuitStage = 1;
    this.scheduleCircuitProbe(FIRST_COOLDOWN_MS);
  }

  scheduleCircuitProbe(delay) {
    this.clearReconnectTimeout();
    this.retryAllowedAt = Date.now() + delay;

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      this.requestCircuitProbe();
    }, delay);
  }

  handleProbeFailure() {
    if (this.circuitStage === 1) {
      this.circuitStage = 2;
      this.scheduleCircuitProbe(SECOND_COOLDOWN_MS);
      return;
    }

    this.circuitStage = 3;
    this.retryAllowedAt = Date.now() + SECOND_COOLDOWN_MS;
    this.clearReconnectTimeout();
  }

  resetCircuit() {
    this.reconnectAttempts = 0;
    this.circuitStage = 0;
    this.retryAllowedAt = 0;
    this.clearReconnectTimeout();
  }

  requestCircuitProbe() {
    if (
      this.circuitStage === 0 ||
      Date.now() < this.retryAllowedAt ||
      !this.isBrowserOnline() ||
      this.disconnectPromise ||
      this.connectPromise ||
      this.hasActiveSession()
    ) {
      return Promise.resolve();
    }

    return this.connect(this.companyId, { isRecoveryProbe: true });
  }

  setupRecoveryListeners() {
    if (this.recoveryListenersAttached || typeof window === "undefined") return;

    window.addEventListener("online", this.handleRecoverySignal);
    window.addEventListener("focus", this.handleRecoverySignal);

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", this.handleVisibilityChange);
    }

    this.recoveryListenersAttached = true;
  }

  removeRecoveryListeners() {
    if (!this.recoveryListenersAttached || typeof window === "undefined") return;

    window.removeEventListener("online", this.handleRecoverySignal);
    window.removeEventListener("focus", this.handleRecoverySignal);

    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    }

    this.recoveryListenersAttached = false;
  }

  handleRecoverySignal() {
    this.requestCircuitProbe();
  }

  handleVisibilityChange() {
    if (document.visibilityState === "visible") {
      this.requestCircuitProbe();
    }
  }

  isBrowserOnline() {
    return typeof navigator === "undefined" || navigator.onLine !== false;
  }

  disconnect({ preserveCallbacks = false } = {}) {
    if (!preserveCallbacks) {
      this.clearCallbacksOnDisconnect = true;
    }

    if (this.disconnectPromise) {
      return this.disconnectPromise;
    }

    this.clearReconnectTimeout();
    this.removeRecoveryListeners();
    this.sessionVersion++;

    const connection = this.connection;
    this.connection = null;
    this.companyId = null;
    this.connectPromise = null;
    this.isConnected = false;
    this.isConnecting = false;

    const stopPromise = connection ? this.stopConnection(connection) : Promise.resolve();

    this.disconnectPromise = stopPromise.finally(() => {
      if (this.disconnectPromise) {
        this.isConnected = false;
        this.isConnecting = false;
        this.resetCircuit();
        if (this.clearCallbacksOnDisconnect) {
          this.callbacks.clear();
        }
        this.clearCallbacksOnDisconnect = false;
        this.disconnectPromise = null;
      }
    });

    return this.disconnectPromise;
  }

  async stopConnection(connection) {
    try {
      await connection.stop();
    } catch (error) {
      console.error("Error stopping SignalR connection:", error);
    }
  }

  clearReconnectTimeout() {
    if (!this.reconnectTimeout) return;

    clearTimeout(this.reconnectTimeout);
    this.reconnectTimeout = null;
  }

  isCurrentSession(connection, sessionVersion) {
    return this.connection === connection && this.sessionVersion === sessionVersion;
  }

  hasActiveSession() {
    const connectionState = this.connection?.state;
    const activeConnectionStates = ["Connected", "Connecting", "Reconnecting", "Disconnecting"];

    return this.isConnected || this.isConnecting || activeConnectionStates.includes(connectionState);
  }

  normalizeCompanyId(companyId) {
    if (companyId === null || companyId === undefined) {
      return null;
    }

    const normalizedCompanyId = String(companyId).trim();
    return normalizedCompanyId || null;
  }

  subscribe(event, callback) {
    if (!this.callbacks.has(event)) {
      this.callbacks.set(event, new Set());
    }
    this.callbacks.get(event).add(callback);
    this.requestCircuitProbe();

    return () => {
      const eventCallbacks = this.callbacks.get(event);
      if (eventCallbacks) {
        eventCallbacks.delete(callback);
        if (eventCallbacks.size === 0) {
          this.callbacks.delete(event);
        }
      }
    };
  }

  notifyCallbacks(event, data) {
    const eventCallbacks = this.callbacks.get(event);
    if (eventCallbacks) {
      eventCallbacks.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in callback for ${event}:`, error);
        }
      });
    }
  }

  getConnectionState() {
    return {
      isConnected: this.isConnected,
      isConnecting: this.isConnecting,
      connectionState: this.connection?.state || "Disconnected",
    };
  }
}

const chatHub = new ChatHub();
export default chatHub;
